import React, { useState, useEffect, useMemo } from 'react';
import { useLocalization } from '../../../../hooks/useLocalization';

interface User {
    id: string;
    name: string;
    email: string;
    phone: string;
    role: 'Admin' | 'Guide' | 'Customer' | 'Staff' | 'Support';
    location: string;
    status: 'Active' | 'Inactive' | 'On Leave';
    lastActive: string;
    createdAt: string;
    avatar?: string;
    bookingsCount: number;
    totalSpent: number;
    provider?: string;
    emailVerified?: boolean;
}

interface UserStats {
    total: number;
    active: number;
    staff: number;
    customers: number;
}

const emptyUser = {
    name: '',
    email: '',
    phone: '',
    role: 'Customer' as const,
    location: '',
    status: 'Active' as const,
    password: ''
};

const UserManagement: React.FC = () => {
    const { language } = useLocalization();

    // Localization labels
    const labels = {
        en: {
            title: 'User Management',
            subtitle: 'Manage staff, guides, and customer accounts',
            addUser: 'Add User',
            loading: 'Loading users...',
            totalUsers: 'Total Users',
            activeNow: 'Active Now',
            staffMembers: 'Staff Members',
            customers: 'Customers',
            searchPlaceholder: 'Search by name, email or location...',
            noUsersFound: 'No Users Found',
            adjustFilters: 'Try adjusting your search or filters',
            getStarted: 'Get started by adding your first user',
            user: 'User',
            role: 'Role',
            location: 'Location',
            status: 'Status',
            lastActive: 'Last Active',
            actions: 'Actions',
            showing: 'Showing',
            of: 'of',
            users: 'users',
            noLocation: 'No location',
            phone: 'Phone',
            memberSince: 'Member Since',
            bookings: 'Bookings',
            totalSpent: 'Total Spent',
            resetPassword: 'Reset Password',
            edit: 'Edit',
            close: 'Close',
            editUser: 'Edit User',
            addNewUser: 'Add New User',
            fullName: 'Full Name',
            email: 'Email',
            newPassword: 'New Password (leave blank to keep current)',
            autoGenerate: 'Password (leave blank to auto-generate)',
            cancel: 'Cancel',
            saveChanges: 'Save Changes',
            passwordGenerated: 'Password Generated',
            passwordGenDesc: 'The new password has been generated. Please copy it and share with the user securely.',
            copyClipboard: 'Copy to Clipboard',
            done: 'Done',
            deleteUser: 'Delete User?',
            deleteConfirm: 'Are you sure you want to delete',
            deactivate: 'Deactivate (Soft Delete)',
            permanentDelete: 'Permanently Delete'
        },
        sv: {
            title: 'Användarhantering',
            subtitle: 'Hantera personal, guider och kundkonton',
            addUser: 'Lägg till Användare',
            loading: 'Laddar användare...',
            totalUsers: 'Totalt Användare',
            activeNow: 'Aktiva Nu',
            staffMembers: 'Personal',
            customers: 'Kunder',
            searchPlaceholder: 'Sök efter namn, e-post eller plats...',
            noUsersFound: 'Inga Användare Hittades',
            adjustFilters: 'Försök justera din sökning eller filter',
            getStarted: 'Kom igång genom att lägga till din första användare',
            user: 'Användare',
            role: 'Roll',
            location: 'Plats',
            status: 'Status',
            lastActive: 'Senast Aktiv',
            actions: 'Åtgärder',
            showing: 'Visar',
            of: 'av',
            users: 'användare',
            noLocation: 'Ingen plats',
            phone: 'Telefon',
            memberSince: 'Medlem Sedan',
            bookings: 'Bokningar',
            totalSpent: 'Totalt Spenderat',
            resetPassword: 'Återställ Lösenord',
            edit: 'Redigera',
            close: 'Stäng',
            editUser: 'Redigera Användare',
            addNewUser: 'Lägg till Ny Användare',
            fullName: 'Fullständigt Namn',
            email: 'E-post',
            newPassword: 'Nytt lösenord (lämna tomt för att behålla)',
            autoGenerate: 'Lösenord (lämna tomt för att generera)',
            cancel: 'Avbryt',
            saveChanges: 'Spara Ändringar',
            passwordGenerated: 'Lösenord Genererat',
            passwordGenDesc: 'Det nya lösenordet har genererats. Kopiera det och dela det säkert med användaren.',
            copyClipboard: 'Kopiera till Urklipp',
            done: 'Klar',
            deleteUser: 'Ta bort Användare?',
            deleteConfirm: 'Är du säker på att du vill ta bort',
            deactivate: 'Inaktivera (Mjuk Radering)',
            permanentDelete: 'Ta bort Permanent'
        }
    };
    const l = labels[language] || labels.en;

    const [users, setUsers] = useState<User[]>([]);
    const [stats, setStats] = useState<UserStats>({ total: 0, active: 0, staff: 0, customers: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState<string>('All');
    const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
    const [newPassword, setNewPassword] = useState<string | null>(null);
    const [formData, setFormData] = useState(emptyUser);
    const [saving, setSaving] = useState(false);

    // Fetch users from API
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            // First check if we have a session
            const response = await fetch('/api/users', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.status === 401 || response.status === 403) {
                // Do not redirect automatically, just show error
                throw new Error('Authentication required or Access Denied. Please ensure you are logged in as Admin.');
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to fetch users');
            }

            const data = await response.json();
            if (data.success) {
                setUsers(data.users);
                setStats(data.stats);
            } else {
                throw new Error(data.message || 'Failed to fetch users');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            if ((err instanceof Error) && err.message.includes('Authentication')) {
                // Just log to console, user sees the error banner
                console.warn('Auth error in UserManagement');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.location.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesRole = filterRole === 'All' || u.role === filterRole;
            return matchesSearch && matchesRole;
        });
    }, [users, searchTerm, filterRole]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400';
            case 'Inactive': return 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400';
            case 'On Leave': return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400';
            default: return 'bg-gray-100 text-gray-600 border-gray-200';
        }
    };

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'Admin': return 'admin_panel_settings';
            case 'Guide': return 'tour';
            case 'Staff': return 'badge';
            case 'Support': return 'support_agent';
            default: return 'person';
        }
    };

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'Admin': return 'text-purple-600 dark:text-purple-400';
            case 'Guide': return 'text-primary';
            case 'Staff': return 'text-blue-600 dark:text-blue-400';
            case 'Support': return 'text-orange-600 dark:text-orange-400';
            default: return 'text-gray-500';
        }
    };

    const handleOpenDetail = (user: User) => {
        setSelectedUser(user);
        setIsDetailOpen(true);
    };

    const handleOpenCreate = () => {
        setFormData(emptyUser);
        setIsEditing(false);
        setIsFormOpen(true);
    };

    const handleOpenEdit = (user: User) => {
        setFormData({
            name: user.name,
            email: user.email,
            phone: user.phone,
            role: user.role,
            location: user.location,
            status: user.status,
            password: ''
        });
        setSelectedUser(user);
        setIsEditing(true);
        setIsFormOpen(true);
    };

    const handleSaveUser = async () => {
        try {
            setSaving(true);
            setError(null);

            const payload: any = {
                email: formData.email,
                name: formData.name,
                phone: formData.phone,
                role: formData.role,
                location: formData.location,
                status: formData.status
            };

            if (formData.password) {
                payload.password = formData.password;
            }

            if (isEditing && selectedUser) {
                // Update user
                const response = await fetch(`/api/users/${selectedUser.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(payload)
                });
                const data = await response.json();
                if (!data.success) {
                    throw new Error(data.message || 'Failed to update user');
                }
            } else {
                // Create user
                const response = await fetch('/api/users', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(payload)
                });
                const data = await response.json();
                if (!data.success) {
                    throw new Error(data.message || 'Failed to create user');
                }
                // Show generated password if applicable
                if (data.generatedPassword) {
                    setNewPassword(data.generatedPassword);
                    setIsPasswordResetOpen(true);
                }
            }

            setIsFormOpen(false);
            fetchUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = async (permanent: boolean = false) => {
        if (!selectedUser) return;
        try {
            setSaving(true);
            const response = await fetch(`/api/users/${selectedUser.id}${permanent ? '?permanent=true' : ''}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to delete user');
            }
            setIsDeleteConfirmOpen(false);
            setIsDetailOpen(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (user: User) => {
        try {
            const response = await fetch(`/api/users/${user.id}/toggle-status`, {
                method: 'PATCH',
                credentials: 'include'
            });
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to toggle status');
            }
            fetchUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser) return;
        try {
            setSaving(true);
            const response = await fetch(`/api/users/${selectedUser.id}/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({})
            });
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to reset password');
            }
            setNewPassword(data.newPassword);
            setIsPasswordResetOpen(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-500 dark:text-gray-400">{l.loading}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-full">
            <div className="max-w-[1400px] mx-auto space-y-6">
                {/* Error Banner */}
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl flex items-center gap-3">
                        <span className="material-icons-round">error</span>
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
                            <span className="material-icons-round">close</span>
                        </button>
                    </div>
                )}

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{l.title}</h1>
                        <p className="text-gray-500 dark:text-gray-400">{l.subtitle}</p>
                    </div>
                    <button onClick={handleOpenCreate} className="px-4 py-2.5 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 flex items-center gap-2 shadow-lg shadow-primary/20 transition-all">
                        <span className="material-icons-round">person_add</span>
                        {l.addUser}
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{l.totalUsers}</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-blue-600 dark:text-blue-400">people</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{l.activeNow}</p>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{stats.active}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-green-600 dark:text-green-400">check_circle</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Staff Members</p>
                                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">{stats.staff}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-purple-600 dark:text-purple-400">badge</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customers</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.customers}</p>
                            </div>
                            <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                <span className="material-icons-round text-orange-600 dark:text-orange-400">shopping_bag</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full md:w-96">
                        <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">search</span>
                        <input
                            type="text"
                            placeholder="Search by name, email or location..."
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-gray-800 dark:text-white"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-4 w-full md:w-auto justify-between">
                        <div className="flex items-center gap-2 overflow-x-auto pb-1">
                            {['All', 'Admin', 'Staff', 'Guide', 'Support', 'Customer'].map(role => (
                                <button
                                    key={role}
                                    onClick={() => setFilterRole(role)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${filterRole === role
                                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                >
                                    {role}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-1 border-l pl-4 border-gray-200 dark:border-gray-700 flex-shrink-0">
                            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                                <span className="material-icons-round">view_list</span>
                            </button>
                            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                                <span className="material-icons-round">grid_view</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Empty State */}
                {filteredUsers.length === 0 && !loading && (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-12 text-center">
                        <span className="material-icons-round text-6xl text-gray-300 dark:text-gray-600 mb-4">people_outline</span>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Users Found</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                            {searchTerm || filterRole !== 'All' ? 'Try adjusting your search or filters' : 'Get started by adding your first user'}
                        </p>
                        <button onClick={handleOpenCreate} className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90">
                            Add User
                        </button>
                    </div>
                )}

                {/* Users - List View */}
                {viewMode === 'list' && filteredUsers.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[900px]">
                            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                <tr>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Active</th>
                                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group cursor-pointer" onClick={() => handleOpenDetail(user)}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center text-white font-bold text-sm">
                                                    {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className={`material-icons-round text-lg ${getRoleColor(user.role)}`}>{getRoleIcon(user.role)}</span>
                                                <span className="text-sm text-gray-700 dark:text-gray-200">{user.role}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{user.location || '-'}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(user.status)}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'Active' ? 'bg-green-500 animate-pulse' : user.status === 'On Leave' ? 'bg-yellow-500' : 'bg-gray-400'}`}></span>
                                                {user.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{user.lastActive}</td>
                                        <td className="px-6 py-4 text-center" onClick={e => e.stopPropagation()}>
                                            <div className="flex items-center justify-center gap-1 opacity-60 group-hover:opacity-100">
                                                <button onClick={() => handleOpenEdit(user)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg text-gray-500 hover:text-primary transition-colors" title="Edit">
                                                    <span className="material-icons-round text-lg">edit</span>
                                                </button>
                                                <button onClick={() => { setSelectedUser(user); handleResetPassword(); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg text-gray-500 hover:text-blue-500 transition-colors" title="Reset Password">
                                                    <span className="material-icons-round text-lg">lock_reset</span>
                                                </button>
                                                <button onClick={() => handleToggleStatus(user)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg text-gray-500 hover:text-orange-500 transition-colors" title="Toggle Status">
                                                    <span className="material-icons-round text-lg">{user.status === 'Active' ? 'pause_circle' : 'play_circle'}</span>
                                                </button>
                                                <button onClick={() => { setSelectedUser(user); setIsDeleteConfirmOpen(true); }} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-gray-500 hover:text-red-500 transition-colors" title="Delete">
                                                    <span className="material-icons-round text-lg">delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="p-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 text-center text-sm text-gray-500 dark:text-gray-400">
                            Showing {filteredUsers.length} of {users.length} users
                        </div>
                    </div>
                )}

                {/* Users - Grid View */}
                {viewMode === 'grid' && filteredUsers.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredUsers.map((user) => (
                            <div key={user.id} onClick={() => handleOpenDetail(user)} className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer group">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                        {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                    </div>
                                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold border ${getStatusColor(user.status)}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'Active' ? 'bg-green-500' : user.status === 'On Leave' ? 'bg-yellow-500' : 'bg-gray-400'}`}></span>
                                        {user.status}
                                    </span>
                                </div>
                                <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors">{user.name}</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{user.email}</p>

                                <div className="mt-3 flex items-center gap-2 text-sm">
                                    <span className={`material-icons-round text-base ${getRoleColor(user.role)}`}>{getRoleIcon(user.role)}</span>
                                    <span className="text-gray-600 dark:text-gray-300">{user.role}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                    <span className="material-icons-round text-sm">location_on</span>
                                    {user.location || 'No location'}
                                </p>

                                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
                                    <span className="text-xs text-gray-400">{user.lastActive}</span>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                                        <button onClick={() => handleOpenEdit(user)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-400 hover:text-primary">
                                            <span className="material-icons-round text-lg">edit</span>
                                        </button>
                                        <button onClick={() => { setSelectedUser(user); setIsDeleteConfirmOpen(true); }} className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-gray-400 hover:text-red-500">
                                            <span className="material-icons-round text-lg">delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* User Detail Modal */}
            {isDetailOpen && selectedUser && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsDetailOpen(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                        <div className="p-6 text-center border-b border-gray-200 dark:border-gray-700">
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 shadow-lg">
                                {selectedUser.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedUser.name}</h2>
                            <p className="text-gray-500 dark:text-gray-400">{selectedUser.email}</p>
                            <div className="flex items-center justify-center gap-2 mt-2">
                                <span className={`material-icons-round text-lg ${getRoleColor(selectedUser.role)}`}>{getRoleIcon(selectedUser.role)}</span>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{selectedUser.role}</span>
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold border ${getStatusColor(selectedUser.status)}`}>{selectedUser.status}</span>
                            </div>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div><span className="text-gray-500 dark:text-gray-400">Phone:</span><p className="font-medium text-gray-900 dark:text-white">{selectedUser.phone || '-'}</p></div>
                                <div><span className="text-gray-500 dark:text-gray-400">Location:</span><p className="font-medium text-gray-900 dark:text-white">{selectedUser.location || '-'}</p></div>
                                <div><span className="text-gray-500 dark:text-gray-400">Member Since:</span><p className="font-medium text-gray-900 dark:text-white">{selectedUser.createdAt}</p></div>
                                <div><span className="text-gray-500 dark:text-gray-400">Last Active:</span><p className="font-medium text-gray-900 dark:text-white">{selectedUser.lastActive}</p></div>
                            </div>
                            {selectedUser.role === 'Customer' && (
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 grid grid-cols-2 gap-4">
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-primary">{selectedUser.bookingsCount}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Bookings</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-gray-900 dark:text-white">€{selectedUser.totalSpent.toLocaleString()}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Spent</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
                            <button onClick={() => handleResetPassword()} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                                <span className="material-icons-round text-lg">lock_reset</span> Reset Password
                            </button>
                            <button onClick={() => { setIsDetailOpen(false); handleOpenEdit(selectedUser); }} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2">
                                <span className="material-icons-round text-lg">edit</span> Edit
                            </button>
                            <button onClick={() => setIsDetailOpen(false)} className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            {isFormOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsFormOpen(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{isEditing ? 'Edit User' : 'Add New User'}</h2>
                            <button onClick={() => setIsFormOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1">
                                <span className="material-icons-round">close</span>
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name*</label>
                                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email*</label>
                                <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {isEditing ? 'New Password (leave blank to keep current)' : 'Password (leave blank to auto-generate)'}
                                </label>
                                <input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder={isEditing ? '••••••••' : 'Auto-generate if empty'} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                                <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                                    <select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value as any })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                                        <option value="Customer">Customer</option>
                                        <option value="Staff">Staff</option>
                                        <option value="Guide">Guide</option>
                                        <option value="Support">Support</option>
                                        <option value="Admin">Admin</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                    <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value as any })} className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                                <input type="text" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} placeholder="City, Country" className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" />
                            </div>
                        </div>
                        <div className="p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
                            <button onClick={() => setIsFormOpen(false)} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700">Cancel</button>
                            <button onClick={handleSaveUser} disabled={!formData.name || !formData.email || saving} className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                                {isEditing ? 'Save Changes' : 'Add User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Password Reset Success Modal */}
            {isPasswordResetOpen && newPassword && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setIsPasswordResetOpen(false); setNewPassword(null); }}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
                        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                            <span className="material-icons-round text-3xl text-green-600 dark:text-green-400">check_circle</span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Password Generated</h3>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">The new password has been generated. Please copy it and share with the user securely.</p>
                        <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-3 mb-4">
                            <code className="text-lg font-mono text-primary font-bold">{newPassword}</code>
                        </div>
                        <button onClick={() => { navigator.clipboard.writeText(newPassword); }} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 mr-2">
                            Copy to Clipboard
                        </button>
                        <button onClick={() => { setIsPasswordResetOpen(false); setNewPassword(null); }} className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90">
                            Done
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {isDeleteConfirmOpen && selectedUser && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setIsDeleteConfirmOpen(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
                        <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                            <span className="material-icons-round text-3xl text-red-600 dark:text-red-400">warning</span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete User?</h3>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Are you sure you want to delete <strong>{selectedUser.name}</strong>?</p>

                        <div className="flex flex-col gap-3 justify-center">
                            <button onClick={handleDeleteUser} disabled={saving} className="w-full px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                                Deactivate (Soft Delete)
                            </button>
                            {selectedUser.role !== 'Admin' && (
                                <button onClick={() => {
                                    // Trigger hard delete logic here or calling param
                                    // Modified handleDeleteUser to accept permanent flag
                                    handleDeleteUser(true);
                                }} disabled={saving} className="w-full px-4 py-3 bg-red-800/80 text-white rounded-lg font-medium hover:bg-red-900 disabled:opacity-50 flex items-center justify-center gap-2">
                                    Permanently Delete
                                </button>
                            )}
                            <button onClick={() => setIsDeleteConfirmOpen(false)} className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
